import { TFunction } from "i18next";
import { useEffect } from "react";
import { APP_DOMAIN, APP_NAME } from "../../utls/constants";
import { seoMetaUtil } from "../../utls/seoMetaUtil";

interface IProfileCustomPageInfos {
  username: string;
  name?: string;
  avatarPath?: string;
  lastModificationDate?: string;
  publicationsCount?: number;
  t: TFunction<"translation", undefined>;
}

const useProfileCustomPageInfos = ({ username, name, avatarPath, lastModificationDate, publicationsCount, t }: IProfileCustomPageInfos) => {

  useEffect(() => {
    const profileName = name || username;
    const randomSeoMeta = seoMetaUtil.getRandomSeoMeta(username, name);
    const pageTitle = randomSeoMeta.title;
    const pageDescription = randomSeoMeta.description;

    document.title = pageTitle;

    let metaDescription = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDescription) {
      metaDescription = document.createElement("meta");
      metaDescription.name = "description";
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = pageDescription;

    document.querySelectorAll('link[rel="canonical"]').forEach((link) => link.remove());
    const linkCanonical = document.createElement("link");
    linkCanonical.rel = "canonical";
    linkCanonical.href = `https://www.${APP_DOMAIN}/p/${username}`;
    document.head.appendChild(linkCanonical);

    let metaRobots = document.querySelector('meta[name="robots"]') as HTMLMetaElement;

    if (!metaRobots) {
      metaRobots = document.createElement("meta");
      metaRobots.name = "robots";
      document.head.appendChild(metaRobots);
    }
    metaRobots.content = "index, follow";

    let scriptJsonLd = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement;
    if (!scriptJsonLd) {
      scriptJsonLd = document.createElement("script");
      scriptJsonLd.type = "application/ld+json";
      document.head.appendChild(scriptJsonLd);
    }
    const profileUrl = `https://www.${APP_DOMAIN}/p/${username}`;
    const displayName = profileName;
    const imageUrl = avatarPath || `https://www.${APP_DOMAIN}/default-avatar.jpg`;

    const profilePageData: any = {
      "@context": "https://schema.org",
      "@type": "ProfilePage",
      "@id": `${profileUrl}#profile`,
      url: profileUrl,
      name: displayName,
      description: pageDescription,
      isPartOf: {
        "@type": "WebSite",
        name: "Twizz",
        url: `https://www.${APP_DOMAIN}`,
      },
      mainEntity: {
        "@type": "Person",
        "@id": `${profileUrl}#person`,
        name: displayName,
        alternateName: `@${username}`,
        description: "Créateur de contenu exclusif sur Twizz.",
        image: imageUrl,
        url: profileUrl,
      },
    };
    
    if (lastModificationDate) {
      profilePageData.dateModified = lastModificationDate;
    }
    
    if (publicationsCount !== undefined && publicationsCount !== null) {
      profilePageData.mainEntity.interactionStatistic = {
        "@type": "InteractionCounter",
        "interactionType": "https://schema.org/WriteAction",
        "userInteractionCount": publicationsCount
      };
    }

    scriptJsonLd.textContent = JSON.stringify([
      profilePageData,
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            item: {
              "@id": `https://www.${APP_DOMAIN}`,
              name: "Twizz App",
            },
          },
          {
            "@type": "ListItem",
            position: 2,
            item: {
              "@id": `https://www.${APP_DOMAIN}/profiles`,
              name: "Twizz Profils",
            },
          },
          {
            "@type": "ListItem",
            position: 3,
            item: {
              "@id": profileUrl,
              name: `${displayName} - @${username}`,
            },
          },
        ],
      },
    ]);

    return () => {
      document.title = APP_NAME;
      if (metaDescription) {
        metaDescription.content = "";
      }
      if (linkCanonical) {
        linkCanonical.remove();
      }
      if (metaRobots) {
        metaRobots.content = "";
      }
      if (scriptJsonLd) {
        scriptJsonLd.remove();
      }
    };
  }, [username, name, avatarPath, lastModificationDate, publicationsCount, t]);
};

export default useProfileCustomPageInfos;
